import { useCallback, useState } from 'react';

import cn from 'classnames';
import evaluateFieldConditions from 'helpers/evaluate-field-conditions';
import { Controller, useWatch } from 'react-hook-form';

import QuestionWrapper from 'components/Form/Questions/QuestionWrapper';
import { Checkbox } from 'components/FormComponents/Checkbox/Checkbox';
import { FormMessage, MessageType } from 'components/FormComponents/FormMessage/FormMessage';
import { IdRequirementsList } from 'components/FormComponents/IdRequirementsList/IdRequirementsList';
import { Textarea } from 'components/FormComponents/Textarea/Textarea';
import { ValidationAlert } from 'components/FormComponents/ValidationAlert/ValidationAlert';
import { ProgressBar } from 'components/ProgressBar/ProgressBar';

export const IdRequirementsWrapper = ({
  field = null,
  brand,
  uploadCode,
  className = '',
  getUpload,
  signUpload,
  createUpload,
  deleteUpload,
  modelParams,
  componentMap,
  control,
  setValue,
  getValues,
  register,
  allValues,
  rejectedData,
  fieldPathIndex = {},
  name = 'id_requirements',
}) => {
  // Import getComponentProps directly to avoid circular dependency
  const { getComponentProps: originalGetComponentProps } = require('helpers/question-config');
  const {
    code,
    inclAltIdInstructions,
    altIdInstructionsCheckboxTitle,
    altIdInstructions,
    altIdFields,
    subFields,
    title,
    minPoints,
    requirementsMet,
    hasIdRequirements,
  } = field || {};

  const watchedValue = useWatch({
    control,
    name: `${code}_not_fulfilled`,
    defaultValue: allValues ? allValues?.[`${code}_not_fulfilled`] : false,
  });

  const watchedNotFulfilledReason = useWatch({
    control,
    name: `${code}_not_fulfilled_reason`,
    defaultValue: allValues ? allValues?.[`${code}_not_fulfilled_reason`] : '',
  });

  const currentFormValue =
    watchedValue ?? (allValues ? allValues?.[`${code}_not_fulfilled`] : false);

  const [pointsMet, setPointsMet] = useState(0);
  const [pointsByComponent, setPointsByComponent] = useState({});

  const uploadHanlders = {
    getCandidateUpload: getUpload,
    signCandidateUpload: signUpload,
    createCandidateUpload: createUpload,
    deleteCandidateUpload: deleteUpload,
  };

  // Handle points change from child components
  const handlePointsChange = useCallback(
    (componentIndex, newPoints) => {
      setPointsByComponent(prev => {
        const updated = { ...prev, [componentIndex]: newPoints };
        const total = Object.values(updated).reduce((sum, points) => sum + points, 0);
        const newPointsMet = total > minPoints ? minPoints : total;
        setPointsMet(newPointsMet);

        // selectedTypes will be updated by the useEffect when id_uploads change
        return updated;
      });
    },
    [minPoints],
  );

  return (
    <div className={cn('d-flex flex-column u-gap-medium')}>
      {minPoints && (
        <ProgressBar
          current={pointsMet}
          total={minPoints}
          label="Points Required"
          showLabel={true}
          showPercentage={true}
          size="medium"
          variant="success"
          animated={true}
        />
      )}

      {inclAltIdInstructions && (
        <div className="d-flex flex-column u-gap-medium">
          <Controller
            name={`${code}_not_fulfilled`}
            control={control}
            defaultValue={currentFormValue}
            render={({ field: { onChange, value } }) => (
              <Checkbox
                name={`${code}_not_fulfilled`}
                checked={value}
                onChange={onChange}
                label={altIdInstructionsCheckboxTitle}
                register={register}
              />
            )}
          />
          {watchedValue && (
            <div className="d-flex flex-column u-gap-default">
              <span className={`primary-text u-font-size-small u-font-weight-500`}>
                Help us understand why you're not able to meet the ID requirements
              </span>
              <Textarea
                defaultValue={allValues?.[`${code}_not_fulfilled_reason`]}
                register={register}
                name={`${code}_not_fulfilled_reason`}
                validators={{ required: true }}
                placeholder="Please explain what's preventing you from providing the required ID or documents…"
                inputProps={{ rows: 3, maxLength: 1000 }}
              />
              {!watchedNotFulfilledReason && (
                <ValidationAlert
                  show={true}
                  message="This field is required and must be 1000 characters or less."
                />
              )}
            </div>
          )}
          {currentFormValue &&
            (altIdFields?.length > 0 ? (
              altIdFields.map((fieldGroup, groupIndex) => (
                <div key={groupIndex}>
                  <div className="d-flex flex-column u-gap-medium u-border u-padding u-border-radius">
                    <div className="d-flex flex-column u-gap-small">
                      <span className="primary-text u-font-size-large u-font-weight-500">
                        {fieldGroup?.title}
                      </span>
                      <span className="secondary-text u-font-size-small u-font-weight-400">
                        {fieldGroup?.description}
                      </span>
                    </div>
                    {fieldGroup?.subFields?.length > 0 ? (
                      fieldGroup.subFields.map((subField, subFieldIndex) => (
                        <QuestionWrapper
                          key={subField?.code || `${groupIndex}-${subFieldIndex}`}
                          question={subField}
                          componentMap={componentMap}
                          questionIndex={subFieldIndex}
                          control={control}
                          register={register}
                          setValue={setValue}
                          getValues={getValues}
                          requirementVariantStyle="text"
                          gap="small"
                          titleSize="small"
                          fieldName={`${code}_[${subFieldIndex}].${subField.code}`}
                          getComponentProps={value =>
                            originalGetComponentProps(
                              subField,
                              value,
                              uploadCode,
                              uploadHanlders,
                              brand,
                              false,
                            )
                          }
                        />
                      ))
                    ) : (
                      <FormMessage
                        message="Our support team will be in touch with you shortly to provide further instructions."
                        type={MessageType.Warning}
                        closeable={false}
                        noMargin
                      />
                    )}
                  </div>
                </div>
              ))
            ) : (
              <FormMessage
                message={
                  altIdInstructions ||
                  'Our support team will be in touch with you shortly to provide further instructions.'
                }
                type={MessageType.Warning}
                closeable={false}
                noMargin
              />
            ))}
        </div>
      )}

      <div
        className={cn('d-flex flex-column u-gap-medium justify-content-center align-items-center', {
          [className]: className,
        })}>
        <div className="u-width-100 d-flex flex-column u-gap-medium">
          {subFields &&
            subFields?.map((subField, index) => {
              // Check if subField has conditions
              let conditionsMet = true;
              if (subField?.conditions) {
                conditionsMet = evaluateFieldConditions(subField.conditions, allValues || {}, {
                  parentCode: code,
                  arrayIndex: null,
                  fieldPathIndex,
                });
              }

              // Skip rendering if conditions aren't met
              if (!conditionsMet) {
                return null;
              }

              return (
                <IdRequirementsList
                  key={index}
                  code={subField?.code}
                  uploadCode={uploadCode}
                  brand={brand}
                  title={subField?.title}
                  description={subField?.description}
                  options={subField.options}
                  getUpload={getUpload}
                  signUpload={signUpload}
                  createUpload={createUpload}
                  deleteUpload={deleteUpload}
                  modelParams={modelParams}
                  hasIdRequirements={subField?.hasIdRequirements}
                  min={subField?.min}
                  requirementsMet={subField?.requirementsMet}
                  wrapperRequirementsMet={requirementsMet}
                  onPointsChange={points => handlePointsChange(index, points)}
                  setValue={setValue}
                  rejectedData={rejectedData}
                />
              );
            })}
        </div>
      </div>
    </div>
  );
};
